/** Notices use permitted saved subject facts, never raw listing fallbacks. */
export interface SingleHomeValuationScope {
  state: 'not_applicable' | 'needs_review'
  message: string
}

export interface ValuationNoticeEvidence {
  gdvPence: unknown
  gdvIndicative?: boolean
  gdvReviewRequired?: boolean
  warnings?: readonly string[]
}

export const NO_SUPPORTED_SUBJECT_GDV = 'No supported subject GDV is available from this market evidence. Nearby sale prices are market context only.'
export const COMPARABLE_BATHROOMS_WARNING = 'Bathroom counts are unavailable for the retained asking comparables; a bathroom match has not been established.'

export function singleHomeValuationScope(subject?: { assetClass?: string | null } | null, inputQuality?: { singleHomeState?: unknown } | null): SingleHomeValuationScope | null {
  const subjectLabel = subject?.assetClass === 'mixed_use' ? 'this mixed-use property'
    : subject?.assetClass === 'commercial' ? 'this commercial property'
      : subject?.assetClass === 'land' ? 'this land lot'
        : subject?.assetClass === 'other' ? 'this specialist property' : null
  if (subjectLabel) return { state: 'not_applicable', message: `Single-home valuation does not apply to ${subjectLabel}.` }
  if (inputQuality?.singleHomeState === 'not_applicable') return { state: 'not_applicable', message: 'Single-home valuation does not apply to this property.' }
  if (inputQuality?.singleHomeState === 'needs_review') return { state: 'needs_review', message: 'Single-home valuation is unavailable until this property’s classification or scope has been resolved.' }
  return null
}

export function valuationWarnings(valuation?: ValuationNoticeEvidence | null, scope?: SingleHomeValuationScope | null): string[] {
  // An omitted/redacted value is not evidence that a subject valuation is absent.
  if (valuation?.gdvPence === null && scope) return [scope.message]
  return valuation?.warnings?.filter(warning => warning !== COMPARABLE_BATHROOMS_WARNING && warning.trim().length > 0)
    .map(warning => valuation.gdvPence === null ? warning.replace(/^GDV uses /, 'Market context includes ') : warning) ?? []
}

export function valuationNoticeContent(valuation?: ValuationNoticeEvidence | null, scope?: SingleHomeValuationScope | null): { title: string; rows: string[] } | null {
  if (!valuation) return null
  const noSubjectGdv = valuation.gdvPence === null
  const warnings = valuationWarnings(valuation, scope)
  if (noSubjectGdv && scope) return {
    title: scope.state === 'not_applicable' ? 'Single-home valuation is not applicable' : 'Single-home valuation needs scope review',
    rows: warnings,
  }
  if (!noSubjectGdv && !valuation.gdvReviewRequired && warnings.length === 0) return null
  const title = noSubjectGdv ? 'Sale evidence is market context'
    : valuation.gdvIndicative ? 'Indicative valuation · review before relying on a bid'
      : valuation.gdvReviewRequired ? 'Valuation needs review' : 'Valuation evidence coverage'
  return { title, rows: noSubjectGdv ? [NO_SUPPORTED_SUBJECT_GDV, ...warnings]
    : warnings.length ? warnings : ['Valuation estimates disagree. Review the evidence before relying on a bid.'] }
}
